import {
  AsyncPropSelector,
  ApiProgressLoader,
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator,
  useCheckFeatureGating
} from '@nile/components';
import { useLazyQuery } from '@apollo/client';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import { omit } from 'lodash';
import {
  Button,
  ButtonGroup,
  Popover,
  Dropdown
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  GET_WHITELISTED_SERVICE_ITEMS,
  AccountClient,
  GET_ELIGIBLE_SERVICE_ITEMS
} from '@nile/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
  white-space: ${(props) => (props.wrap ? 'normal' : 'nowrap')};
`;

function TextCell(props) {
  const { cell, params, _ticket } = props;
  const allProps = params || cell.column;
  const flags = useFlags();
  const {
    editable,
    field,
    actions,
    accessor,
    entity,
    editMode,
    clientId
  } = allProps;
  const ticket = _ticket || cell.row.original;
  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const [popoverActive, setPopoverActive] = useState(false);
  const [updatedProperties, setUpdatedProperties] = useState({});
  const [apiProgress, setApiProgress] = useState(false);
  const [apiResult, setApiResult] = useState('');
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const [allServiceItems, setAllServiceItems] = useState([]);
  const [getEligibleServiceItems, { loading, data, error }] = useLazyQuery(
    contractsNew ? GET_ELIGIBLE_SERVICE_ITEMS : GET_WHITELISTED_SERVICE_ITEMS,
    {
      client: AccountClient,
      onCompleted(res) {
        setAllServiceItems(
          res?.[
            contractsNew
              ? 'getEligibleServiceCatalogItems'
              : 'getWhitelistedServiceItems'
          ]?.items || []
        );
      }
    }
  );

  const updateSelectedProperties = (key, value) => {
    setUpdatedProperties({
      ...updatedProperties,
      [key]: value
    });
  };

  const updateSelection = (f, val) => {
    let serialisedValue = null;
    if (field.isCustom) {
      updateSelectedProperties('customFields', val);
    } else {
      switch (field.name) {
        case 'techGroup':
          serialisedValue = { groupId: val.groupId, name: val.name };
          break;
        case 'technician':
          serialisedValue = { userId: val.userId };
          break;
        case 'sop':
          serialisedValue = { sopId: val.sopId, name: val.name };
          break;
        default:
          serialisedValue = val.name;
      }
      updateSelectedProperties(field.name, serialisedValue);
    }
  };

  const setApiComplete = (state) => {
    if (props.showProgress) {
      setApiProgress(false);
      setApiResult(state);
    }
    setTimeout(() => {
      setApiResult('');
    }, 2000);
  };

  const getTag = () => {
    const fieldValue = updatedProperties[field.name] || ticket[field.name];
    const defaultValue = fieldValue
      ? typeof fieldValue === 'object'
        ? {
            name: fieldValue.name,
            label: fieldValue.name,
            value: fieldValue.name
          }
        : { name: fieldValue, label: fieldValue, value: fieldValue }
      : '';
    return (
      <div className="min-w-200 max-w-250 fg1 pr10 pl10 mt-8 mb8 ">
        <AsyncPropSelector
          defaultValue={defaultValue}
          updateSelection={updateSelection}
          ticket={ticket}
          field={field}
          entity={entity || 'Ticket'}
        />
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedProperties({});
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                if (props.showProgress) {
                  setApiProgress(true);
                }
                actions.save &&
                  actions.save(
                    updatedProperties,
                    ticket,
                    props.data,
                    setApiComplete
                  );
                togglePopoverActive();
                setUpdatedProperties({});
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  const renderValue = (displayVal, suffix) => {
    const updatedDisplayVal = `${displayVal}${suffix || ''}`;
    if (field?.name === 'requester' && ticket?.source === 'Agent') {
      return 'System';
    }
    if (cell?.column?.isBold) {
      return <b>{updatedDisplayVal}</b>;
    }
    return updatedDisplayVal;
  };
  //TODO :: render value in a proper way for NA - vignesh
  const displayVal = cell
    ? props.column.subAccessor
      ? cell.value[props.column.subAccessor]
      : cell.value
      ? cell.value.name || cell.value
      : ''
    : ticket[accessor]
    ? typeof ticket[accessor] === 'object'
      ? ticket[accessor].name
        ? ticket[accessor].name
        : ticket[accessor].firstName
        ? ticket[accessor].firstName + ' ' + ticket[accessor].lastName
        : ticket[accessor].label
      : ticket[accessor]
    : '';
  const options = allServiceItems.map((el) => {
    return {
      ...el,
      name: el.name,
      label: el.name,
      value: el.name
    };
  });

  return (
    <Wrap className={'cell'}>
      {editable && editMode ? (
        <>
          <Dropdown
            className="rule-fields"
            value={
              options.find(
                (el) =>
                  el.itemId === cell?.row?.original?.serviceCatalogItem?.itemId
              ) || {
                ...cell?.row?.original?.serviceCatalogItem,
                value: cell?.row?.original?.serviceCatalogItem.name,
                label: cell?.row?.original?.serviceCatalogItem.name
              }
            }
            onMenuOpen={() => {
              getEligibleServiceItems({
                variables: contractsNew
                  ? {
                      input: {
                        listInfo: {
                          sort: {
                            attribute: 'name',
                            order: 'ASC'
                          }
                        }
                      }
                    }
                  : {
                      listInfo: {},
                      clientId: clientId
                    }
              });
            }}
            onChange={(val) => {
              actions.onChange({
                key: 'serviceCatalogItem',
                value: { ...omit(val, ['label', 'value']) },
                field: cell.row.original,
                meta: {
                  tax: val.salesTax,
                  taxable: contractsNew ? val.taxable : val.salesTaxEnabled
                }
              });
            }}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            components={{ IndicatorSeparator, DropdownIndicator }}
            options={options}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            name="valueselector"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
            noOptionsMessage={() => {
              return loading ? 'Loading...' : 'No options';
            }}
          />
        </>
      ) : (
        <div
          className={`${cell?.column?.wrap ? '' : 'ellipsis'}`}
          title={displayVal}
        >
          {displayVal ? (
            renderValue(displayVal, cell?.column?.suffix)
          ) : (
            <DisabledTag>NA</DisabledTag>
          )}
        </div>
      )}
    </Wrap>
  );
}

export default TextCell;
